import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileText, BarChart2, Home } from 'lucide-react';
import { eggWatchAdminAPI } from '../../../services/eggWatchAdminAPI';
import './EggWatchAdmin.css';

const EggWatchExport: React.FC = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState<string | null>(null);
  const navigate = useNavigate();

  const setQuickRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const handleExport = async (exportType: string) => {
    try {
      setExporting(exportType);
      const params: { export_type: string; start_date?: string; end_date?: string; format?: string } = {
        export_type: exportType,
        format: 'csv',
      };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await eggWatchAdminAPI.exportData(params);
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `egg-watch-${exportType}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(null);
    }
  };

  const exports = [
    {
      type: 'daily_logs',
      icon: <FileText size={32} color="#8b0000" />,
      title: 'Daily Logs',
      desc: 'All egg production logs with lay rates, feed, and weather tags',
    },
    {
      type: 'all_farms',
      icon: <Home size={32} color="#2563eb" />,
      title: 'Farm Summary',
      desc: 'All enrolled farms with current metrics, trends, and status',
    },
    {
      type: 'feed_performance',
      icon: <BarChart2 size={32} color="#059669" />,
      title: 'Feed Performance',
      desc: 'Feed formula comparison with lay rates and breed breakdowns',
    },
  ];

  return (
    <div className="egg-watch-admin">
      <button className="ewa-back-button" onClick={() => navigate('/planner/egg-watch')}>
        <ArrowLeft size={16} /> Back to Egg Watch
      </button>

      <div className="ewa-header">
        <div className="ewa-header-title">
          <Download className="ewa-header-icon" />
          <div>
            <h1>Export Data</h1>
            <p className="ewa-header-subtitle">Download Egg Watch data for analysis</p>
          </div>
        </div>
      </div>

      {/* Date Range */}
      <div className="ewa-card">
        <div className="ewa-card-header">
          <h2>Date Range</h2>
        </div>
        <div className="ewa-card-content">
          <div className="ewa-date-range-inputs">
            <div className="ewa-date-input-group">
              <label>Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <div className="ewa-date-input-group">
              <label>End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <div className="ewa-quick-ranges">
            <button onClick={() => setQuickRange(7)}>Last 7 days</button>
            <button onClick={() => setQuickRange(14)}>Last 14 days</button>
            <button onClick={() => setQuickRange(30)}>Last 30 days</button>
            <button onClick={() => setQuickRange(90)}>Last 90 days</button>
          </div>
        </div>
      </div>

      {/* Export Options */}
      <div className="ewa-card">
        <div className="ewa-card-header">
          <h2>Choose Export</h2>
        </div>
        <div className="ewa-card-content">
          <div className="ewa-export-grid">
            {exports.map(exp => (
              <div key={exp.type} className="ewa-export-option">
                {exp.icon}
                <h3>{exp.title}</h3>
                <p>{exp.desc}</p>
                <button
                  className="ewa-btn-primary"
                  disabled={exporting !== null}
                  onClick={() => handleExport(exp.type)}
                >
                  {exporting === exp.type ? 'Exporting...' : (
                    <><Download size={14} /> Download CSV</>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EggWatchExport;
